import * as React from "react"
import { cloneElement } from "react"
import { cc } from "../../utils/jsx"
import "./progress-bar.css"

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  min?: number
  now?: number
  max?: number
  label?: React.ReactNode
  visuallyHidden?: boolean
  striped?: boolean
  animated?: boolean
  variant?: "success" | "danger" | "warning" | "info" | string
  isChild?: boolean
}

const ROUND_PRECISION = 1000

function getPercentage(now: number, min: number, max: number) {
  const percentage = ((now - min) / (max - min)) * 100
  return Math.round(percentage * ROUND_PRECISION) / ROUND_PRECISION
}

function renderProgressBar(
  {
    min,
    now,
    max,
    label,
    visuallyHidden,
    striped,
    animated,
    className,
    style,
    ...props
  }: ProgressBarProps,
  ref: React.Ref<HTMLDivElement>
) {
  return (
    <div
      ref={ref}
      {...props}
      role="progressbar"
      className={cc("progress-bar", className ?? "", {
        "progress-bar-animated": !!animated,
        "progress-bar-striped": !!(animated || striped)
      })}
      style={{ width: `${getPercentage(now!, min!, max!)}%`, ...style }}
      aria-valuenow={now}
      aria-valuemin={min}
      aria-valuemax={max}
    >
      {visuallyHidden ? (
        <span className="visually-hidden">{label}</span>
      ) : (
        label
      )}
    </div>
  )
}

const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ isChild = false, ...rest }: ProgressBarProps, ref) => {
    const props = {
      min: 0,
      max: 100,
      animated: false,
      visuallyHidden: false,
      striped: false,
      ...rest
    }

    if (isChild) {
      return renderProgressBar(props, ref)
    }

    const {
      min,
      now,
      max,
      label,
      visuallyHidden,
      striped,
      animated,
      variant,
      className,
      children,
      ...wrapperProps
    } = props

    return (
      <div ref={ref} {...wrapperProps} className={cc("progress", className ?? "")}>
        {children
          ? map(children, (child) => cloneElement(child, { isChild: true }))
          : renderProgressBar(
              {
                min,
                now,
                max,
                label,
                visuallyHidden,
                striped,
                animated,
                variant
              },
              ref
            )}
      </div>
    )
  }
)

ProgressBar.displayName = "ProgressBar"

export default ProgressBar

function map<P = any>(
  children: React.ReactNode,
  func: (el: React.ReactElement<P>, index: number) => React.ReactNode
) {
  let index = 0

  return React.Children.map(children, (child) =>
    React.isValidElement<P>(child) ? func(child, index++) : child
  )
}
